import { useQuery } from "@tanstack/react-query";
import { useProjectContext } from "../context/ProjectContext";
import { analyticsService } from "../services/analytics";

export const analyticsQueryKeys = {
	base: ["analytics"] as const,
	realtime: (projectId?: string, modelConfigId?: string) =>
		[...analyticsQueryKeys.base, "realtime", projectId, modelConfigId] as const,
	charts: (projectId?: string, period?: string, modelConfigId?: string) =>
		[
			...analyticsQueryKeys.base,
			"charts",
			projectId,
			period,
			modelConfigId,
		] as const,
};

/**
 * Hook to fetch real-time stats (last 5 minutes)
 * Polls every 10 seconds
 */
export const useRealTimeStats = (modelConfigId?: string) => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;

	return useQuery({
		queryKey: analyticsQueryKeys.realtime(projectId, modelConfigId),
		queryFn: () => analyticsService.getRealTimeStats(projectId!, modelConfigId),
		enabled: Boolean(projectId),
		staleTime: 0, // Always consider stale for real-time data
		refetchInterval: 10000, // Poll every 10 seconds
	});
};

/**
 * Hook to fetch chart data for visualizations
 */
export const useChartData = (
	period: "hour" | "day" | "week" | "month" = "day",
	modelConfigId?: string,
) => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;

	return useQuery({
		queryKey: analyticsQueryKeys.charts(projectId, period, modelConfigId),
		queryFn: () =>
			analyticsService.getChartData(projectId!, period, modelConfigId),
		enabled: Boolean(projectId),
		staleTime: 1000 * 60, // 1 minute
	});
};
